<template>
	<VAutocomplete
		:value="address"
		:search-input.sync="search"
		:loading="state === StateEnum.PENDING"
		:items="items"
		:error-messages="validationRules"
		hide-no-data
		outlined
		no-filter
		clearable
		return-object
		item-value="id"
		label="Adresse et code postal"
		class="search-address-input"
		data-cy="search-address-input"
		background-color="white"
		append-icon=""
		@change="addressSelected"
	>
		<template #prepend-inner>
			<VIcon color="grey-lighten-20">
				{{ searchIcon }}
			</VIcon>
		</template>

		<template #item="{ item }">
			<span v-html="formatAddress(item)" />
		</template>

		<template #selection="{ item }">
			<span v-html="formatAddress(item)" />
		</template>
	</VAutocomplete>
</template>

<script lang="ts">
	import Vue, { PropType } from 'vue';
	import Component, { mixins } from 'vue-class-component';

	import { mdiMagnify } from '@mdi/js';

	import { AddressData } from '@/types';
	import { searchAddress } from '@/services/address/api';
	import { FeatureProperties } from '@/services/address/types';

	import { StateEnum } from '@cnamts/vue-dot/src/constants/enums/StateEnum';
	import { debounce } from '@cnamts/vue-dot/src/functions/debounce';

	const Props = Vue.extend({
		props: {
			value: {
				type: Object as PropType<AddressData>,
				default: undefined
			}
		}
	});

	const MixinsDeclaration = mixins(Props);

	@Component<SearchAddressInput>({
		model: {
			prop: 'value',
			event: 'change'
		},
		computed: {
			validationRules(): string[] {
				if (!this.search || this.search.length < 10 || this.items.length) {
					return [];
				}
				return ['Aucune ville n\'est reconnue : vérifiez votre saisie'];
			}
		},
		watch: {
			search(): void {
				this.debounced();
			},
			value: {
				handler(value: AddressData): void {
					this.address = value;

					if (!this.items.length) {
						this.items = [value];
					}
				},
				immediate: true
			}
		}
	})
	export default class SearchAddressInput extends MixinsDeclaration {
		StateEnum = StateEnum;
		searchIcon = mdiMagnify;

		search: string | null = null;
		items: AddressData[] = [];

		address: AddressData | null = null;

		state: StateEnum = StateEnum.IDLE;

		debounced = debounce(() => this.searchApi(), 500);

		parseItems(entries: FeatureProperties[]): AddressData[] {
			return entries.map((entry) => {
				return {
					name: entry.street,
					city: entry.city,
					zipCode: entry.zipcode.toString(),
					coordinates: {
						lat: entry.y,
						long: entry.x
					}
				};
			});
		}

		searchApi(): void {
			if (!this.search || this.search.length < 3) {
				return;
			}

			this.state = StateEnum.PENDING;

			searchAddress(this.search)
				.then((response) => {
					this.items = this.parseItems(response.data.results);
					this.state = StateEnum.RESOLVED;
				})
				.catch(() => this.state = StateEnum.REJECTED);
		}

		formatAddress(address?: AddressData): string | null {
			if (!address) {
				return null;
			}

			const zipCode = address.zipCode ? `(${address.zipCode})` : '';

			let result = `<b>${address.city} ${zipCode} </b>`;

			if (address.name !== undefined && address.name.length > 0) {
				result += `- ${address.name}`;
			}

			return result;
		}

		addressSelected(address: AddressData): void {
			this.$emit('change', address);
		}
	}
</script>

<style lang="scss">
@import '@cnamts/design-tokens/dist/tokens';
.v-messages__message{
	font-size: 14px;
}
.search-address-input {
	font-size: 1.125rem !important;
}
.v-list {
  padding-bottom: 0 !important;
}
.v-autocomplete__content {
  border-radius: 4px !important;
	&:after {
		content: 'Vous ne trouvez pas votre ville ? Ajoutez un code postal.';
		border-left: solid 4px $vd-yellow-darken-20;
		padding: 0 12px;
		display: flex;
		align-items: center;
		background-color: $vd-yellow-lighten-80;
		height: 52px;
		line-height: 18px;
	}
}
</style>
